import {ApptSlot} from "@services/monolith/availability";
import {minBy} from "lodash";
import useCurrentPatient from "@page-containers/booking/utils/useCurrentPatient";
import {useSlotsBatch} from "@services/cloudflare/slots";
import {useMemo} from "react";
import cartesian from "../../../utils/cartesian";
import {chooseSpecialty} from "@components/Locations/usePairLocsWithSlots";

type MinimalLocation = {id?: string | null};
export type WithNextSlot<T> = T & {nextSlot?: ApptSlot};
const soonestOf = (slots?: ApptSlot[]) => (slots ? minBy(slots, slot => slot.time) ?? null : null);

/**
 * Find the "next available" slots for each location and return them.
 */
export const useNextAvailable = <T extends MinimalLocation>({
  locations,
  reasonId,
  specialtyId,
}: {
  locations?: T[];
  reasonId?: string;
  specialtyId: string;
}) => {
  const {patient, loading: patientLoading} = useCurrentPatient();
  const patientId = patient?.id;

  const requests = patientLoading
    ? []
    : locations?.map(location => ({
        specialtyId: specialtyId,
        locationId: location.id ?? undefined,
        reasonId: reasonId ?? undefined,
        patientId,
      })) ?? [];

  const {data, isLoading, error} = useSlotsBatch(requests);

  return useMemo(() => {
    const soonestSlots = data?.map(soonestOf);
    const locationsWithSlots = locations?.map((location, i) => ({
      ...location,
      nextSlot: soonestSlots?.[i] ?? undefined,
    }));

    return {
      data: locationsWithSlots,
      isLoading,
      error,
    };
  }, [data, isLoading, locations, error]);
};

type MinimalReason = {id?: string | null; specialtyIds: string[]};

export const useNextAvailableForReasons = <T extends MinimalReason>({
  locationIds,
  reasons,
}: {
  locationIds: string[];
  reasons?: T[];
}) => {
  const {patient, loading: patientLoading} = useCurrentPatient();
  const patientId = patient?.id;

  const requests = patientLoading
    ? []
    : cartesian(locationIds, reasons ?? []).map(([locationId, reason]) => ({
        specialtyId: chooseSpecialty(reason.specialtyIds),
        locationId,
        reasonId: reason.id ?? undefined,
        patientId,
      })) ?? [];
  const {data, isLoading, error} = useSlotsBatch(requests);
  return useMemo(() => {
    const soonestSlot = soonestOf(data?.flat());
    return {
      data: soonestSlot,
      isLoading,
      error,
    };
  }, [data, isLoading, error]);
};
